import React, {
  useEffect,
  useMemo,
  useState
} from 'react';
import {
  get,
  post,
  put
} from '../utils/api';
import {
  useAuth
} from '../context/AuthContext';

const STATUS_COLORS = {
  pending: {
    bg: '#FFF3DC',
    color: '#8B5E0A',
    label: 'Pending'
  },
  received: {
    bg: '#E8F5EB',
    color: '#2E6B3A',
    label: 'Received'
  },
  partial: {
    bg: '#EDF4FF',
    color: '#1E4FAD',
    label: 'Partial'
  },
  discrepancy: {
    bg: '#FDEAEA',
    color: '#8F2E24',
    label: 'Discrepancy'
  },
  closed: {
    bg: '#F2EFE7',
    color: '#556B2F',
    label: 'Closed'
  }
};

const emptyReceipt = {
  purchase_order_id: '',
  purchase_order_line_id: '',
  delivery_document_ref: '',
  notes: ''
};

const numberValue = value =>
  Number(value || 0);

export default function InventoryReceipts() {
  const {
    user
  } = useAuth();

  const canWrite = [
    'super_admin',
    'admin',
    'inventory_officer'
  ].includes(user?.role);

  const queryParams =
    new URLSearchParams(
      window.location.search
    );

  const requestedPoId =
    queryParams.get(
      'purchase_order_id'
    ) || '';

  const [
    receipts,
    setReceipts
  ] = useState([]);

  const [
    skus,
    setSkus
  ] = useState([]);

  const [
    purchaseOrders,
    setPurchaseOrders
  ] = useState([]);

  const [
    selectedPO,
    setSelectedPO
  ] = useState(null);

  const [
    modal,
    setModal
  ] = useState(null);

  const [
    confirmModal,
    setConfirmModal
  ] = useState(null);

  const [
    loading,
    setLoading
  ] = useState(true);

  const [
    loadingPO,
    setLoadingPO
  ] = useState(false);

  const [
    saving,
    setSaving
  ] = useState(false);

  const [
    error,
    setError
  ] = useState('');

  const [
    statusFilter,
    setStatusFilter
  ] = useState('');

  const [
    search,
    setSearch
  ] = useState('');

  const [
    autoOpened,
    setAutoOpened
  ] = useState(false);

  const load = async () => {
    setLoading(true);

    try {
      const [
        receiptResult,
        skuResult,
        poResult
      ] = await Promise.all([
        get('/inventory-control/receipts'),
        get('/inventory-control/skus'),
        get('/purchase-orders')
      ]);

      setReceipts(
        receiptResult.data || []
      );

      setSkus(
        skuResult.data || []
      );

      setPurchaseOrders(
        poResult.data || []
      );
    } catch (requestError) {
      setError(
        requestError.message ||
        'Unable to load Inventory Receipts'
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const availablePOs =
    useMemo(
      () =>
        purchaseOrders.filter(
          po =>
            [
              'approved',
              'partially_received'
            ].includes(po.status)
        ),
      [purchaseOrders]
    );

  const loadPO = async poId => {
    if (!poId) {
      setSelectedPO(null);
      setModal({
        ...emptyReceipt
      });
      return;
    }

    setLoadingPO(true);
    setError('');

    try {
      const result =
        await get(
          '/inventory-control/receipts/po-lookup/' +
          poId
        );

      const po =
        result.data;

      setSelectedPO(po);

      setModal(current => ({
        ...emptyReceipt,
        ...(current || {}),
        purchase_order_id: poId,
        purchase_order_line_id: '',
      }));
    } catch (requestError) {
      setSelectedPO(null);
      setError(
        requestError.message ||
        'Unable to load the Purchase Order'
      );
    } finally {
      setLoadingPO(false);
    }
  };

  useEffect(() => {
    if (
      requestedPoId &&
      !autoOpened &&
      purchaseOrders.length
    ) {
      setAutoOpened(true);
      setModal({
        ...emptyReceipt,
        purchase_order_id:
          requestedPoId
      });
      loadPO(requestedPoId);
    }
  }, [
    requestedPoId,
    autoOpened,
    purchaseOrders
  ]);

  const openNew = () => {
    setError('');
    setSelectedPO(null);
    setModal({
      ...emptyReceipt
    });
  };

  const selectedLine =
    selectedPO?.lines?.find(
      line =>
        String(line.id) ===
        String(
          modal?.purchase_order_line_id
        )
    ) || null;

  const selectLine = lineId => {
    setModal(current => ({
      ...current,
      purchase_order_line_id:
        lineId,
    }));
  };

  const saveNew = async () => {
    setError('');

    if (
      !modal?.purchase_order_id ||
      !modal?.purchase_order_line_id
    ) {
      setError(
        'Select an approved Purchase Order and one outstanding material PO line.'
      );
      return;
    }

    setSaving(true);

    try {
      await post(
        '/inventory-control/receipts',
        modal
      );

      setModal(null);
      setSelectedPO(null);

      window.history.replaceState(
        {},
        '',
        '/inventory-receipts'
      );

      await load();
    } catch (requestError) {
      setError(
        requestError.message ||
        'Unable to create Inventory Receipt'
      );
    } finally {
      setSaving(false);
    }
  };

  const openConfirm = receipt => {
    setError('');

    setConfirmModal({
      ...receipt,
      delivered_quantity:
        receipt.ordered_quantity || '',
      accepted_quantity:
        receipt.ordered_quantity || '',
      rejected_quantity: 0,
      damaged_quantity: 0,
      variance_reason: '',
      notes: receipt.notes || ''
    });
  };

  const confirmReceipt = async () => {
    setError('');

    const accepted =
      numberValue(
        confirmModal.accepted_quantity
      );

    const rejected =
      numberValue(
        confirmModal.rejected_quantity
      );

    const damaged =
      numberValue(
        confirmModal.damaged_quantity
      );

    const delivered =
      numberValue(
        confirmModal.delivered_quantity
      );

    if (
      accepted < 0 ||
      rejected < 0 ||
      damaged < 0 ||
      delivered < 0
    ) {
      setError(
        'Receipt quantities cannot be negative.'
      );
      return;
    }

    if (
      Math.abs(
        accepted +
        rejected +
        damaged -
        delivered
      ) > 0.0001
    ) {
      setError(
        'Accepted, rejected and damaged quantities must equal the delivered quantity.'
      );
      return;
    }

    if (
      accepted !==
        numberValue(
          confirmModal.ordered_quantity
        ) &&
      !String(
        confirmModal.variance_reason || ''
      ).trim()
    ) {
      setError(
        'Provide a reason when the accepted quantity differs from the outstanding PO quantity.'
      );
      return;
    }

    setSaving(true);

    try {
      const result =
        await put(
          '/inventory-control/receipts/' +
          confirmModal.id +
          '/confirm',
          confirmModal
        );

      setConfirmModal(null);

      await load();

      if (result.bill_number) {
        window.alert(
          'Receipt confirmed. Purchase Order is complete and supplier bill ' +
          result.bill_number +
          ' was created.'
        );
      }
    } catch (requestError) {
      setError(
        requestError.message ||
        'Unable to confirm Inventory Receipt'
      );
    } finally {
      setSaving(false);
    }
  };

  const filtered =
    receipts.filter(receipt => {
      const matchesStatus =
        !statusFilter ||
        receipt.status === statusFilter;

      const searchText =
        search.toLowerCase();

      const matchesSearch =
        !searchText ||
        receipt.receipt_number
          ?.toLowerCase()
          .includes(searchText) ||
        receipt.supplier_name
          ?.toLowerCase()
          .includes(searchText) ||
        receipt.sku_description
          ?.toLowerCase()
          .includes(searchText) ||
        receipt.po_number
          ?.toLowerCase()
          .includes(searchText) ||
        receipt.po_line_description
          ?.toLowerCase()
          .includes(searchText);

      return (
        matchesStatus &&
        matchesSearch
      );
    });

  const counts = {
    pending:
      receipts.filter(
        receipt =>
          receipt.status === 'pending'
      ).length,
    received:
      receipts.filter(
        receipt =>
          receipt.status === 'received'
      ).length,
    partial:
      receipts.filter(
        receipt =>
          receipt.status === 'partial'
      ).length,
    discrepancy:
      receipts.filter(
        receipt =>
          receipt.status ===
          'discrepancy'
      ).length
  };

  return (
    <div>
      <div className="flex-between mb-lg">
        <div>
          <h2 className="page-title">
            Inventory Receipts
          </h2>
          <p
            className="text-muted text-sm"
            style={{marginTop:4}}
          >
            Physically verify approved Purchase Orders before stock and supplier bills are recorded
          </p>
        </div>

        {canWrite && (
          <button
            className="btn btn-primary"
            onClick={openNew}
          >
            + Receive Approved PO
          </button>
        )}
      </div>

      {error && !modal && !confirmModal && (
        <div className="alert alert-error mb-lg">
          {error}
        </div>
      )}

      <div
        style={{
          background:'#EDF4FF',
          border:'1px solid #B8CCF0',
          color:'#1E4FAD',
          borderRadius:10,
          padding:14,
          marginBottom:18,
          fontSize:12,
          lineHeight:1.6
        }}
      >
        <strong>
          Controlled receipt rule:
        </strong>{' '}
        Every normal supplier receipt must reference an approved Purchase Order and PO line. Only physically accepted quantities enter stock. A supplier bill is created only after every PO line is fully received.
      </div>

      <div
        style={{
          display:'grid',
          gridTemplateColumns:
            'repeat(auto-fill,minmax(140px,1fr))',
          gap:10,
          marginBottom:20
        }}
      >
        {Object.entries(counts).map(
          ([status, count]) => {
            const style =
              STATUS_COLORS[status];

            return (
              <button
                key={status}
                type="button"
                onClick={() =>
                  setStatusFilter(
                    statusFilter === status
                      ? ''
                      : status
                  )
                }
                style={{
                  textAlign:'left',
                  background:
                    statusFilter === status
                      ? style.bg
                      : '#fff',
                  border:
                    `1px solid ${style.color}44`,
                  borderRadius:10,
                  padding:'12px 16px',
                  cursor:'pointer'
                }}
              >
                <div
                  style={{
                    fontSize:9,
                    fontWeight:700,
                    letterSpacing:'.1em',
                    textTransform:'uppercase',
                    color:style.color,
                    marginBottom:4
                  }}
                >
                  {style.label}
                </div>

                <div
                  style={{
                    fontFamily:
                      'Cormorant Garamond,serif',
                    fontSize:'1.6rem',
                    fontWeight:700,
                    color:style.color
                  }}
                >
                  {count}
                </div>
              </button>
            );
          }
        )}
      </div>

      <input
        className="form-control"
        value={search}
        onChange={event =>
          setSearch(event.target.value)
        }
        placeholder="Search receipt, PO, supplier, material or SKU..."
        style={{
          maxWidth:430,
          marginBottom:16
        }}
      />

      <div className="table-wrap">
        {loading ? (
          <div className="loading">
            Loading Inventory Receipts...
          </div>
        ) : (
          <table>
            <thead>
              <tr>
                <th>Receipt</th>
                <th>Purchase Order</th>
                <th>PO Line</th>
                <th>SKU</th>
                <th>Supplier</th>
                <th className="amt">Outstanding</th>
                <th className="amt">Accepted</th>
                <th>Status</th>
                <th>Date</th>
                {canWrite && <th></th>}
              </tr>
            </thead>

            <tbody>
              {!filtered.length ? (
                <tr>
                  <td colSpan="10">
                    <div className="empty-state">
                      <h3>
                        No controlled receipts found
                      </h3>
                      <p>
                        Begin from an approved Purchase Order.
                      </p>
                    </div>
                  </td>
                </tr>
              ) : (
                filtered.map(receipt => {
                  const style =
                    STATUS_COLORS[
                      receipt.status
                    ] ||
                    STATUS_COLORS.pending;

                  return (
                    <tr key={receipt.id}>
                      <td
                        style={{
                          fontWeight:700,
                          fontFamily:'monospace'
                        }}
                      >
                        {receipt.receipt_number}
                      </td>

                      <td>
                        {receipt.po_number || '—'}
                      </td>

                      <td>
                        {receipt.po_line_description ||
                          '—'}
                      </td>

                      <td>
                        <div
                          style={{
                            fontWeight:600
                          }}
                        >
                          {receipt.sku_description ||
                            '—'}
                        </div>
                        <div
                          className="text-muted text-xs"
                        >
                          {receipt.unit_of_measure ||
                            ''}
                        </div>
                      </td>

                      <td>
                        {receipt.supplier_name ||
                          '—'}
                      </td>

                      <td className="amt">
                        {numberValue(
                          receipt.ordered_quantity
                        ).toLocaleString()}
                      </td>

                      <td className="amt">
                        {receipt.accepted_quantity ==
                        null
                          ? '—'
                          : numberValue(
                              receipt.accepted_quantity
                            ).toLocaleString()}
                      </td>

                      <td>
                        <span
                          style={{
                            background:style.bg,
                            color:style.color,
                            borderRadius:20,
                            padding:'3px 8px',
                            fontSize:9,
                            fontWeight:700,
                            textTransform:'uppercase'
                          }}
                        >
                          {style.label}
                        </span>
                      </td>

                      <td>
                        {receipt.created_at
                          ? new Date(
                              receipt.created_at
                            ).toLocaleDateString(
                              'en-NG'
                            )
                          : '—'}
                      </td>

                      {canWrite && (
                        <td>
                          {receipt.status ===
                            'pending' && (
                            <button
                              className="btn btn-sm btn-primary"
                              onClick={() =>
                                openConfirm(
                                  receipt
                                )
                              }
                            >
                              Count & Confirm
                            </button>
                          )}
                        </td>
                      )}
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        )}
      </div>

      {modal && (
        <div
          className="modal-overlay"
          onClick={event =>
            event.target ===
              event.currentTarget &&
            setModal(null)
          }
        >
          <div
            className="modal"
            style={{maxWidth:620}}
          >
            <div className="modal-hdr">
              <span className="modal-title">
                Receive Approved Purchase Order
              </span>
              <button
                className="btn btn-ghost btn-sm"
                onClick={() =>
                  setModal(null)
                }
              >
                ✕
              </button>
            </div>

            <div className="modal-body">
              {error && (
                <div className="alert alert-error">
                  {error}
                </div>
              )}

              <div
                style={{
                  background:'#FFF3DC',
                  borderRadius:8,
                  padding:12,
                  marginBottom:16,
                  fontSize:12,
                  color:'#6B4A08'
                }}
              >
                Creating this record does not change stock. Stock changes only after physical counting and confirmation.
              </div>

              <div className="form-group">
                <label className="form-label">
                  Approved Purchase Order *
                </label>
                <select
                  className="form-control"
                  value={
                    modal.purchase_order_id
                  }
                  onChange={event =>
                    loadPO(
                      event.target.value
                    )
                  }
                  disabled={loadingPO}
                >
                  <option value="">
                    Select approved PO...
                  </option>

                  {availablePOs.map(po => (
                    <option
                      key={po.id}
                      value={po.id}
                    >
                      {po.po_number}
                      {' — '}
                      {po.supplier_name ||
                        'Supplier'}
                      {' — '}
                      {String(po.status)
                        .replace(/_/g, ' ')}
                    </option>
                  ))}
                </select>
              </div>

              {loadingPO && (
                <div className="loading">
                  Loading Purchase Order...
                </div>
              )}

              {selectedPO && (
                <>
                  <div
                    style={{
                      background:'#F9F8F5',
                      borderRadius:8,
                      padding:12,
                      marginBottom:16,
                      fontSize:12,
                      lineHeight:1.6
                    }}
                  >
                    <strong>
                      {selectedPO.po_number}
                    </strong>
                    <br />
                    Supplier:{' '}
                    {selectedPO.supplier_name}
                  </div>

                  <div className="form-group">
                    <label className="form-label">
                      Outstanding PO Line *
                    </label>
                    <select
                      className="form-control"
                      value={
                        modal.purchase_order_line_id
                      }
                      onChange={event =>
                        selectLine(
                          event.target.value
                        )
                      }
                    >
                      <option value="">
                        Select outstanding line...
                      </option>

                      {(selectedPO.lines || []).map(
                        line => (
                          <option
                            key={line.id}
                            value={line.id}
                          >
                            {line.material_name ||
                              line.description}
                            {' — Remaining: '}
                            {numberValue(
                              line.remaining_quantity
                            ).toLocaleString()}
                            {' '}
                            {line.material_unit ||
                              ''}
                          </option>
                        )
                      )}
                    </select>
                  </div>
                </>
              )}

              {selectedLine && (
                <div
                  style={{
                    background:'#F9F8F5',
                    borderRadius:8,
                    padding:12,
                    marginBottom:16,
                    fontSize:12,
                    lineHeight:1.6
                  }}
                >
                  <strong>
                    Inventory Material
                  </strong>
                  <br />
                  {selectedLine.material_name ||
                    selectedLine.description}
                  {' — '}
                  {numberValue(
                    selectedLine.remaining_quantity
                  ).toLocaleString()}
                  {' '}
                  {selectedLine.material_unit || ''}
                  {' remaining'}

                  <div
                    style={{
                      color:'#556B2F',
                      marginTop:5
                    }}
                  >
                    The material is taken directly from the approved Purchase Order line. No separate SKU selection is required.
                  </div>
                </div>
              )}

              <div className="form-group">
                <label className="form-label">
                  Supplier Delivery Document
                </label>
                <input
                  className="form-control"
                  value={
                    modal.delivery_document_ref
                  }
                  onChange={event =>
                    setModal(current => ({
                      ...current,
                      delivery_document_ref:
                        event.target.value
                    }))
                  }
                  placeholder="Waybill, delivery note or supplier invoice reference"
                />
              </div>

              <div className="form-group">
                <label className="form-label">
                  Initial Notes
                </label>
                <textarea
                  className="form-control"
                  rows={2}
                  value={modal.notes}
                  onChange={event =>
                    setModal(current => ({
                      ...current,
                      notes:
                        event.target.value
                    }))
                  }
                  placeholder="Condition on arrival or other preliminary notes"
                />
              </div>
            </div>

            <div className="modal-ftr">
              <button
                className="btn btn-outline"
                onClick={() =>
                  setModal(null)
                }
              >
                Cancel
              </button>

              <button
                className="btn btn-primary"
                onClick={saveNew}
                disabled={
                  saving ||
                  !modal.purchase_order_id ||
                  !modal.purchase_order_line_id
                }
              >
                {saving
                  ? 'Creating...'
                  : 'Create Pending Receipt'}
              </button>
            </div>
          </div>
        </div>
      )}

      {confirmModal && (
        <div
          className="modal-overlay"
          onClick={event =>
            event.target ===
              event.currentTarget &&
            setConfirmModal(null)
          }
        >
          <div
            className="modal"
            style={{maxWidth:620}}
          >
            <div className="modal-hdr">
              <span className="modal-title">
                Physically Count and Confirm
              </span>
              <button
                className="btn btn-ghost btn-sm"
                onClick={() =>
                  setConfirmModal(null)
                }
              >
                ✕
              </button>
            </div>

            <div className="modal-body">
              {error && (
                <div className="alert alert-error">
                  {error}
                </div>
              )}

              <div
                style={{
                  background:'#F9F8F5',
                  borderRadius:8,
                  padding:12,
                  marginBottom:16,
                  fontSize:12,
                  lineHeight:1.6
                }}
              >
                <strong>
                  {confirmModal.po_number}
                </strong>
                <br />
                {confirmModal.po_line_description}
                <br />
                Outstanding quantity:{' '}
                <strong>
                  {numberValue(
                    confirmModal.ordered_quantity
                  ).toLocaleString()}
                </strong>{' '}
                {confirmModal.unit_of_measure}
              </div>

              <div
                style={{
                  display:'grid',
                  gridTemplateColumns:
                    'repeat(2,minmax(0,1fr))',
                  gap:10
                }}
              >
                <div className="form-group">
                  <label className="form-label">
                    Delivered Quantity *
                  </label>
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    className="form-control"
                    value={
                      confirmModal.delivered_quantity
                    }
                    onChange={event =>
                      setConfirmModal(current => ({
                        ...current,
                        delivered_quantity:
                          event.target.value
                      }))
                    }
                  />
                </div>

                <div className="form-group">
                  <label className="form-label">
                    Accepted Quantity *
                  </label>
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    className="form-control"
                    value={
                      confirmModal.accepted_quantity
                    }
                    onChange={event =>
                      setConfirmModal(current => ({
                        ...current,
                        accepted_quantity:
                          event.target.value
                      }))
                    }
                    autoFocus
                  />
                </div>

                <div className="form-group">
                  <label className="form-label">
                    Rejected Quantity
                  </label>
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    className="form-control"
                    value={
                      confirmModal.rejected_quantity
                    }
                    onChange={event =>
                      setConfirmModal(current => ({
                        ...current,
                        rejected_quantity:
                          event.target.value
                      }))
                    }
                  />
                </div>

                <div className="form-group">
                  <label className="form-label">
                    Damaged Quantity
                  </label>
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    className="form-control"
                    value={
                      confirmModal.damaged_quantity
                    }
                    onChange={event =>
                      setConfirmModal(current => ({
                        ...current,
                        damaged_quantity:
                          event.target.value
                      }))
                    }
                  />
                </div>
              </div>

              {numberValue(
                confirmModal.accepted_quantity
              ) !==
                numberValue(
                  confirmModal.ordered_quantity
                ) && (
                <div className="form-group">
                  <label className="form-label">
                    Variance Reason *
                  </label>
                  <textarea
                    className="form-control"
                    rows={2}
                    value={
                      confirmModal.variance_reason
                    }
                    onChange={event =>
                      setConfirmModal(current => ({
                        ...current,
                        variance_reason:
                          event.target.value
                      }))
                    }
                    placeholder="Explain shortage, rejection, damage or other difference"
                  />
                </div>
              )}

              <div className="form-group">
                <label className="form-label">
                  Receipt Notes
                </label>
                <textarea
                  className="form-control"
                  rows={2}
                  value={confirmModal.notes}
                  onChange={event =>
                    setConfirmModal(current => ({
                      ...current,
                      notes:
                        event.target.value
                    }))
                  }
                  placeholder="Packaging condition, physical count or supplier observations"
                />
              </div>

              <div
                style={{
                  background:'#FDEAEA',
                  color:'#8F2E24',
                  borderRadius:8,
                  padding:12,
                  fontSize:12,
                  lineHeight:1.6
                }}
              >
                <strong>Permanent action:</strong>{' '}
                only the accepted quantity will enter usable stock. Rejected and damaged quantities will not be added.
              </div>
            </div>

            <div className="modal-ftr">
              <button
                className="btn btn-outline"
                onClick={() =>
                  setConfirmModal(null)
                }
              >
                Cancel
              </button>

              <button
                className="btn btn-primary"
                style={{
                  background:'#2E6B3A',
                  border:'none'
                }}
                onClick={confirmReceipt}
                disabled={saving}
              >
                {saving
                  ? 'Confirming...'
                  : 'Confirm Accepted Stock'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
